/* platform/OrganizationDetail — tenant detail with 6 tabs. Route: /platform/organizations/:orgId */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { NexusIcon } from '../shared/ui/NexusIcon';
import { Btn, Pill } from '../shared/ui';
import { useToast } from '../shared/feedback';
import { StatCard, TabBar, Th, TenancyModal } from './_ui';
import {
  statusTone, PLAN_TONES, pct,
  orgLicenses, orgWorkspaces, orgMembers,
  workspaceMembers,
} from './platformStore';
import * as orgApi from '../api/organization.api.js';
import * as licenseApi from '../api/license.api.js';
import * as authApi from '../api/auth.api.js';
import * as subApi from '../api/subscription.api.js';
import * as planApi from '../api/plan.api.js';

const LIC_TYPE_ENUM  = { 'User Based': 'USER', 'Agent Based': 'AGENT', 'Bot Based': 'BOT', 'Workspace': 'WORKSPACE', 'Storage': 'STORAGE', 'API': 'API', 'Message': 'MESSAGE', 'Custom': 'CUSTOM' };
const LIC_TYPE_LABEL = { USER: 'User', AGENT: 'Agent', BOT: 'Bot', WORKSPACE: 'Workspace', STORAGE: 'Storage', API: 'API', MESSAGE: 'Message', CUSTOM: 'Custom' };
const LIC_STATUS_TONE = { ACTIVE: 'green', SUSPENDED: 'amber', EXPIRED: 'red', CANCELLED: 'gray' };

const TABS = [
  { id: 'overview',    label: 'Overview',    icon: 'Home'         },
  { id: 'commercial',  label: 'Commercial',  icon: 'CreditCard'   },
  { id: 'workspaces',  label: 'Workspaces',  icon: 'LayoutGrid'   },
  { id: 'users',       label: 'Users',       icon: 'Users'        },
  { id: 'audit',       label: 'Audit Logs',  icon: 'Activity'     },
  { id: 'settings',    label: 'Settings',    icon: 'Settings'     },
];

const BILLING_CYCLE_LABEL = { MONTHLY: 'Monthly', ANNUAL: 'Annual', CUSTOM: 'Custom' };
const PLAN_STATUS_TONE    = { ACTIVE: 'green', INACTIVE: 'amber', ARCHIVED: 'gray' };
const SUB_STATUS_ACTIONS = {
  TRIAL:     ['activate'],
  ACTIVE:    ['suspend', 'expire', 'cancel'],
  SUSPENDED: ['reactivate', 'cancel'],
  EXPIRED:   ['renew'],
  CANCELLED: ['renew'],
};

const fmtDate = (d) => d ? String(d).split('T')[0] : '—';

const EMPTY_PROVISION = { firstName: '', lastName: '', email: '', phone: '' };

const orgStatusColor = (s) => ({
  ACTIVE: '#0E8C5A', ORG_ADMIN_PENDING: '#E08A00', DRAFT: '#6E3FBA',
  SUSPENDED: '#C26A1F', ARCHIVED: '#64748B',
}[s] || '#94A3B8');

const licRenewalStatus = (renews) => {
  const d = renews ? Math.ceil((new Date(renews) - new Date()) / 86400000) : null;
  if (d === null)  return { label: 'No expiry', tone: 'gray'  };
  if (d < 0)       return { label: 'Expired',   tone: 'red'   };
  if (d <= 30)     return { label: d + 'd left', tone: 'amber' };
  return            { label: renews,             tone: 'green' };
};

/* Read-only label/value row */
const InfoRow = ({ label, value }) => (
  <div className="nx-form-row">
    <label>{label}</label>
    <span>{value}</span>
  </div>
);

export default function OrganizationDetail({ data }) {
  const { orgId }  = useParams();
  const navigate   = useNavigate();
  const toast      = useToast();

  const { user } = (typeof window.useAuth === 'function' ? window.useAuth() : {});
  const isSAOrPA = user?.platformRole === 'SUPER_ADMIN' || user?.platformRole === 'PLATFORM_ADMIN';

  const [searchParams] = useSearchParams();
  const [tab, setTab]                       = useState(searchParams.get('tab') || 'overview');
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [livePlans,       setLivePlans]     = useState([]);
  const [loadingPlans,    setLoadingPlans]  = useState(false);
  const [archiveConfirm, setArchiveConfirm] = useState(false);
  const [restoreConfirm, setRestoreConfirm] = useState(false);
  const [suspendSubConfirm, setSuspendSubConfirm] = useState(false);
  const [expireSubConfirm, setExpireSubConfirm]   = useState(false);
  const [cancelSubConfirm, setCancelSubConfirm]   = useState(false);
  const [provisionModal, setProvisionModal] = useState(false);
  const [provisionForm, setProvisionForm]   = useState(EMPTY_PROVISION);
  const [provisionBusy, setProvisionBusy]   = useState(false);
  const [inviteUrl, setInviteUrl]           = useState(null);
  const [selectedWsId, setSelectedWsId]         = useState(null);
  const [selectedMember, setSelectedMember]     = useState(null);
  const [editLicModal, setEditLicModal]         = useState(null);
  const [editLicNew, setEditLicNew]             = useState(0);
  const [editLicReason, setEditLicReason]       = useState('');
  const [deleteLicConfirm, setDeleteLicConfirm] = useState(null);
  const [removeMemberConfirm, setRemoveMemberConfirm] = useState(null);
  const [suspendOrgConfirm, setSuspendOrgConfirm]     = useState(false);
  const [planChangeConfirm, setPlanChangeConfirm]     = useState(false);
  const [addLicModal, setAddLicModal]           = useState(null);
  const [addLicSeats, setAddLicSeats]           = useState(10);

  const org = data.state.organizations.find(o => o.id === orgId);

  // useEffect MUST be unconditional — placed here before any early return to satisfy
  // React Rules of Hooks. The !org guard prevents it from running until the org is
  // loaded into the store (handles the transient loading window after data.reload()).
  useEffect(() => {
    if (!org || tab !== 'commercial' || !isSAOrPA || livePlans.length > 0) return;
    setLoadingPlans(true);
    planApi.list({ status: 'ACTIVE', pageSize: 100 })
      .then(res => { const d = Array.isArray(res) ? res : (res?.data ?? []); setLivePlans(d); })
      .catch(() => {/* silently fall back to empty */})
      .finally(() => setLoadingPlans(false));
  }, [tab, isSAOrPA]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!org) {
    if (data.state.loading) {
      return (
        <div className="nx-cc-page">
          <div className="nx-cc-skeleton" style={{ height: 32, width: 180, borderRadius: 6, marginBottom: 20 }} />
          <div className="nx-cc-skeleton" style={{ height: 72, borderRadius: 10, marginBottom: 16 }} />
          <div className="nx-cc-skeleton" style={{ height: 40, borderRadius: 6, marginBottom: 20 }} />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12 }}>
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="nx-cc-skeleton" style={{ height: 72, borderRadius: 8 }} />
            ))}
          </div>
        </div>
      );
    }
    return (
      <div className="nx-cc-page">
        <button className="nx-cc-back" onClick={() => navigate('/platform/organizations')}>
          <NexusIcon name="ChevronLeft" size={14} /> Back to Organizations
        </button>
        <div className="nx-empty" style={{ marginTop: 40 }}>
          <NexusIcon name="Building2" size={24} /><span>Organization not found.</span>
        </div>
      </div>
    );
  }

  const lics     = orgLicenses(data.state, org.id);
  const wss      = orgWorkspaces(data.state, org.id);
  const members  = orgMembers(data.state, org.id);
  const orgAudit = data.state.audit.filter(a => a.target && a.target.includes(org.name));
  const partner  = data.state.partners.find(p => p.id === org.partnerId);
  const branding = data.state.branding?.[org.id];
  const userLic          = lics.find(l => l.type === 'USER');
  const userLicPurchased = userLic?.purchased ?? 0;
  const userLicUsed      = userLic?.used      ?? 0;
  const isArchived = org.status === 'ARCHIVED';

  const update = (patch) => data.update(s => ({
    ...s,
    organizations: s.organizations.map(o => o.id === org.id ? { ...o, ...patch } : o),
  }));

  const syncField = (field, value) =>
    orgApi.update(org.id, { [field]: value }).catch(() =>
      toast({ tone: 'warning', icon: 'AlertCircle', title: 'Sync failed' }));

  /* ── Provision org admin ──────────────────────────────────── */
  const handleProvision = async () => {
    setProvisionBusy(true);
    try {
      const result = await orgApi.provisionAdmin(org.id, provisionForm);
      update({
        ownerId:    result.user.id,
        owner:      result.user.name,
        ownerEmail: result.user.email,
        status:     result.orgStatus,
      });
      setInviteUrl(window.location.origin + '/invite/' + result.inviteToken);
      data.addAudit({ action: 'provisioned org admin', target: org.name + ' · ' + result.user.email, cat: 'Org', sev: 'info' });
      toast({ tone: 'success', icon: 'UserCheck', title: 'Org Admin Created', desc: 'Invite link generated — share it with the admin.' });
      setProvisionModal(false);
      setProvisionForm(EMPTY_PROVISION);
    } catch (err) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Provision failed', desc: err?.response?.data?.message || err.message });
    } finally {
      setProvisionBusy(false);
    }
  };

  /* ── Archive ──────────────────────────────────────────────── */
  const handleArchive = async () => {
    try {
      await orgApi.archive(org.id);
      update({ status: 'ARCHIVED' });
      data.addAudit({ action: 'archived org', target: org.name, cat: 'Org', sev: 'warning' });
      toast({ tone: 'warning', icon: 'Archive', title: 'Organization Archived', desc: org.name });
      setArchiveConfirm(false);
    } catch (err) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Archive failed', desc: err.message });
    }
  };

  /* ── Restore ──────────────────────────────────────────────── */
  const handleRestore = async () => {
    try {
      await orgApi.restore(org.id);
      update({ status: 'SUSPENDED' });
      data.addAudit({ action: 'restored org', target: org.name, cat: 'Org', sev: 'info' });
      toast({ tone: 'success', icon: 'RotateCcw', title: 'Organization Restored', desc: org.name + ' is now suspended. Activate to bring it live.' });
      setRestoreConfirm(false);
    } catch (err) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Restore failed', desc: err.message });
    }
  };

  return (
    <div className="nx-cc-page">
      <button className="nx-cc-back" onClick={() => navigate('/platform/organizations')}>
        <NexusIcon name="ChevronLeft" size={14} /> Back to Organizations
      </button>

      {/* ── Hero header ─────────────────────────────────────── */}
      <div className="nx-cc-org-hero">
        <div className="nx-cc-org-logo">{org.name.split(' ').map(w => w[0]).slice(0, 2).join('')}</div>
        <div className="nx-cc-org-meta">
          <h2 className="nx-cc-page-title">{org.name}</h2>
          <div className="nx-cc-org-meta-row">
            <Pill tone={statusTone(org.status)}>{org.status.replace(/_/g, ' ')}</Pill>
            {org.plan && <Pill tone={PLAN_TONES[org.plan] || 'gray'}>{org.plan}</Pill>}
            {!org.plan && <Pill tone="gray">No Plan</Pill>}
            {org.region   && <span className="nx-muted nx-mini">{org.region}</span>}
            {org.industry && <span className="nx-muted nx-mini">{org.industry}</span>}
          </div>
        </div>

        {isArchived ? (
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Pill tone="gray">Read-only · Archived</Pill>
            {isSAOrPA && (
              <Btn variant="secondary" icon="RotateCcw" onClick={() => setRestoreConfirm(true)}>Restore</Btn>
            )}
          </div>
        ) : (
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            {org.status === 'ACTIVE' && (
              <Btn variant="ghost" icon="AlertCircle" onClick={() => setSuspendOrgConfirm(true)}>Suspend</Btn>
            )}
            {org.status === 'SUSPENDED' && (
              <Btn variant="secondary" icon="Check" onClick={async () => {
                try {
                  await orgApi.activate(org.id);
                  update({ status: 'ACTIVE' });
                  data.addAudit({ action: 'activated org', target: org.name, cat: 'Org', sev: 'info' });
                  toast({ tone: 'success', icon: 'Check', title: 'Organization Activated' });
                } catch (err) { toast({ tone: 'error', icon: 'AlertCircle', title: 'Action failed', desc: err.message }); }
              }}>Activate</Btn>
            )}
            {isSAOrPA && <Btn variant="ghost" icon="Archive" onClick={() => setArchiveConfirm(true)}>Archive</Btn>}
          </div>
        )}
      </div>

      <TabBar tabs={TABS} active={tab} onChange={setTab} />

      <div className="nx-cc-tab-body">

        {/* ── OVERVIEW (read-only) ─────────────────────────────── */}
        {tab === 'overview' && (
          <div>
            <div className="nx-cc-stat-grid" style={{ gridTemplateColumns: 'repeat(3,1fr)', marginBottom: 16 }}>
              <StatCard label="Status"  value={org.status.replace(/_/g, ' ')} icon="Activity"  color={orgStatusColor(org.status)} />
              <StatCard
                label="Seats"
                value={userLicPurchased > 0 ? pct(userLicUsed, userLicPurchased) + '%' : '—'}
                sub={userLicPurchased > 0 ? userLicUsed + ' / ' + userLicPurchased + ' seats' : 'No user license'}
                icon="Key"
                color="#6E3FBA" />
              <StatCard
                label="Members"
                value={org.users}
                sub={userLic ? 'of ' + userLicPurchased + ' licensed' : 'no user license'}
                icon="Users"
                color="#0E8C5A" />
            </div>
            <div className="nx-form" style={{ maxWidth: 580 }}>
              <InfoRow label="Organization ID"   value={<span className="nx-cell is-mono">{org.id}</span>} />
              <InfoRow label="Industry"          value={org.industry || '—'} />
              <InfoRow label="Country / TZ"      value={[org.country, org.timezone].filter(Boolean).join(' · ') || '—'} />
              <InfoRow label="Created"           value={org.created || '—'} />
              <InfoRow label="Org Admin"         value={org.owner
                ? org.owner + (org.ownerEmail ? ' · ' + org.ownerEmail : '')
                : <Pill tone="amber">Not assigned</Pill>} />
              <InfoRow label="Partner"           value={partner ? partner.name + ' (' + partner.tier + ')' : '— Direct —'} />
              <InfoRow label="Users"      value={userLic ? org.users + ' / ' + userLicPurchased + ' licensed' : org.users + ' (no user license)'} />
              <InfoRow label="Workspaces" value={org.maxWorkspaces != null ? wss.length + ' / ' + org.maxWorkspaces : wss.length + ' (unlimited)'} />
              <InfoRow label="Branding"   value={branding ? branding.primary + ' · ' + branding.logoText : 'Default'} />
            </div>
          </div>
        )}

        {/* ── COMMERCIAL ──────────────────────────────────────── */}
        {tab === 'commercial' && (
          !isSAOrPA ? (
            <div className="nx-empty" style={{ marginTop: 40 }}>
              <NexusIcon name="Lock" size={24} />
              <span>Commercial management requires Super Admin or Platform Admin access.</span>
            </div>
          ) : (
          <div style={{ maxWidth: 720 }}>

            {/* Error state */}
            {data.state.error && (
              <div style={{
                padding: '10px 14px', borderRadius: 8, marginBottom: 16,
                background: 'var(--nx-danger-tint, rgba(220,38,38,0.05))',
                border: '1px solid var(--nx-danger, #DC2626)',
                display: 'flex', gap: 8, alignItems: 'center',
                color: 'var(--nx-danger, #DC2626)',
              }}>
                <NexusIcon name="AlertCircle" size={14} />
                <span style={{ fontSize: 13 }}>Data load failed: {data.state.error}. Showing cached data — refresh to retry.</span>
              </div>
            )}

            {/* ── Commercial quick-scan summary ── */}
            <div className="nx-cc-stat-grid" style={{ gridTemplateColumns: 'repeat(4,1fr)', marginBottom: 16 }}>
              <StatCard
                icon="Tag"      label="Plan"
                value={org.plan || 'No Plan'}
                sub={org.planTier || '—'}
                color={PLAN_TONES[org.plan] || 'gray'}
              />
              <StatCard
                icon="Package"  label="Subscription"
                value={org.subscriptionStatus || 'None'}
                sub={org.billingCycle ? (org.billingCycle.charAt(0) + org.billingCycle.slice(1).toLowerCase()) : '—'}
                color={org.subscriptionStatus === 'ACTIVE' ? 'green' : org.subscriptionStatus === 'TRIAL' ? 'blue' : 'amber'}
              />
              <StatCard
                icon="Key"      label="Seats"
                value={userLicPurchased > 0 ? `${userLicUsed} / ${userLicPurchased}` : '—'}
                sub={userLicPurchased > 0 ? `${Math.max(0, userLicPurchased - userLicUsed)} available` : 'No user license'}
                color={userLicPurchased > 0 && userLicUsed / userLicPurchased >= 0.9 ? 'red' : 'green'}
              />
              <StatCard
                icon="Calendar" label="Renewal"
                value={org.renewalAt ? String(org.renewalAt).split('T')[0] : '—'}
                sub={org.autoRenew ? 'Auto-renew on' : org.autoRenew === false ? 'Auto-renew off' : '—'}
                color="purple"
              />
            </div>

            {/* ── Current Plan card ── */}
            <div className="nx-cc-chart-card" style={{ marginBottom: 16 }}>
              <div className="nx-cc-chart-head">
                <span className="nx-cc-chart-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <NexusIcon name="Tag" size={14} /> Current Plan
                </span>
                <Pill tone={PLAN_TONES[org.plan] || 'gray'}>{org.plan || 'No Plan'}</Pill>
                {org.planTier && <Pill tone="blue">{org.planTier}</Pill>}
                {org.planStatus && org.planStatus !== 'ACTIVE' && (
                  <Pill tone={PLAN_STATUS_TONE[org.planStatus] || 'gray'}>{org.planStatus}</Pill>
                )}
              </div>
              <div className="nx-form" style={{ marginTop: 14 }}>
                <InfoRow label="Plan Name"    value={org.planDisplayName || org.plan || '—'} />
                {org.planDescription && (
                  <InfoRow label="Description" value={<span style={{ color: 'var(--nx-text-muted)', fontStyle: 'italic' }}>{org.planDescription}</span>} />
                )}
                <InfoRow label="Tier"         value={org.planTier || '—'} />
                <InfoRow label="Plan Status"  value={
                  org.planStatus
                    ? <Pill tone={PLAN_STATUS_TONE[org.planStatus] || 'gray'}>{org.planStatus}</Pill>
                    : '—'
                } />
                <InfoRow label="Version"      value={org.planVersion != null ? 'v' + org.planVersion : '—'} />
                <InfoRow label="Currency"     value={org.currency || '—'} />
                <InfoRow label="Plan ID"      value={org.planId ? <span className="nx-cell is-mono" style={{ fontSize: 11 }}>{org.planId}</span> : '—'} />
                <InfoRow label="Assigned"     value={fmtDate(org.startsAt)} />
                <InfoRow label="Feature Count" value={org.planFeatureKeys?.length ?? 0} />
                <InfoRow label="Capabilities" value={
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    <Pill tone={org.planBrandingAllowed     ? 'green' : 'gray'}>Branding</Pill>
                    <Pill tone={org.planCustomDomainAllowed ? 'green' : 'gray'}>Custom Domain</Pill>
                    <Pill tone={org.planAiEnabled           ? 'green' : 'gray'}>AI</Pill>
                    <Pill tone={org.planAnalyticsEnabled    ? 'green' : 'gray'}>Analytics</Pill>
                  </div>
                } />
                <InfoRow label="Limits" value={
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '3px 20px' }}>
                    {[
                      ['Storage',     org.planMaxStorageGb          != null ? org.planMaxStorageGb + ' GB'                          : 'Unlimited'],
                      ['API / day',   org.planMaxApiCallsPerDay      != null ? org.planMaxApiCallsPerDay.toLocaleString()            : 'Unlimited'],
                      ['Msg / mo',    org.planMaxMessagesPerMonth    != null ? org.planMaxMessagesPerMonth.toLocaleString()          : 'Unlimited'],
                      ['Agents',      org.planMaxAgents              != null ? String(org.planMaxAgents)                            : 'Unlimited'],
                      ['Bots',        org.planMaxBots                != null ? String(org.planMaxBots)                              : 'Unlimited'],
                      ['Workspaces',  org.planMaxWorkspaces          != null ? String(org.planMaxWorkspaces)                        : 'Unlimited'],
                    ].map(([lbl, val]) => (
                      <span key={lbl} style={{ fontSize: 12 }}>
                        <span style={{ color: 'var(--nx-text-muted)', marginRight: 4 }}>{lbl}:</span>
                        <span style={{ color: val === 'Unlimited' ? 'var(--nx-text-faint)' : 'var(--nx-text)' }}>{val}</span>
                      </span>
                    ))}
                  </div>
                } />
                <InfoRow label="Feature Keys"  value={
                  org.planFeatureKeys?.length > 0 ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {['crm', 'ai', 'channels', 'analytics', 'org', 'developer', 'storage', 'platform'].map(domain => {
                        const count = (org.planFeatureKeys || []).filter(k => k.startsWith(domain + '.')).length;
                        if (!count) return null;
                        const toneMap = { crm: 'blue', ai: 'purple', channels: 'green', analytics: 'amber', org: 'blue', developer: 'gray', storage: 'gray', platform: 'gray' };
                        return <Pill key={domain} tone={toneMap[domain] || 'gray'}>{domain} · {count}</Pill>;
                      })}
                    </div>
                  ) : <span className="nx-muted">No feature keys — plan may not be assigned</span>
                } />
              </div>
            </div>

            {/* ── Current Subscription card ── */}
            <div className="nx-cc-chart-card" style={{ marginBottom: 16 }}>
              <div className="nx-cc-chart-head">
                <span className="nx-cc-chart-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <NexusIcon name="Package" size={14} /> Current Subscription
                </span>
                {org.subscriptionStatus
                  ? <Pill tone={statusTone(org.subscriptionStatus)}>{org.subscriptionStatus}</Pill>
                  : <Pill tone="gray">No Subscription</Pill>}
              </div>
              {!org.subscriptionId ? (
                <div style={{ marginTop: 12, display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 14px', borderRadius: 8, background: 'var(--nx-surface-alt)', border: '1px solid var(--nx-border)' }}>
                  <NexusIcon name="Package" size={16} style={{ color: 'var(--nx-text-muted)', flexShrink: 0, marginTop: 2 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>No subscription provisioned</div>
                    <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginBottom: 10 }}>
                      This organization was created without a subscription. Run provisioning to assign the default plan and trial licenses automatically.
                    </div>
                    <Btn variant="secondary" size="sm" icon="Zap" onClick={async () => {
                      try {
                        const result = await subApi.migrateMissing();
                        await data.reload();
                        toast({ tone: 'success', icon: 'Zap', title: 'Subscription Provisioned', desc: `${result.provisioned ?? 0} new subscriptions · ${result.licensesCreated ?? 0} licenses created` });
                      } catch (err) {
                        toast({ tone: 'error', icon: 'AlertCircle', title: 'Provisioning failed', desc: err.message });
                      }
                    }}>Provision Subscription</Btn>
                  </div>
                </div>
              ) : (
                <>
                  <div className="nx-form" style={{ marginTop: 14 }}>
                    <InfoRow label="Trial" value={
                      org.subscriptionStatus === 'TRIAL'
                        ? <Pill tone="blue">Active Trial</Pill>
                        : <span style={{ color: 'var(--nx-text-faint)' }}>—</span>
                    } />
                    <InfoRow label="Trial End"     value={fmtDate(org.trialEndsAt)} />
                    <InfoRow label="Billing Cycle" value={org.billingCycle ? BILLING_CYCLE_LABEL[org.billingCycle] || org.billingCycle : '—'} />
                    <InfoRow label="Start Date"    value={fmtDate(org.startsAt)} />
                    <InfoRow label="End Date"      value={fmtDate(org.subExpiresAt)} />
                    <InfoRow label="Renewal Date"  value={fmtDate(org.renewalAt)} />
                    <InfoRow label="Auto-Renew"    value={org.autoRenew != null ? (org.autoRenew ? 'Enabled' : 'Disabled') : '—'} />
                    <InfoRow label="Contract Ref"  value={org.contractRef ? <span className="nx-cell is-mono" style={{ fontSize: 11 }}>{org.contractRef}</span> : '—'} />
                    <InfoRow label="Billing Ref"   value={org.billingRef  ? <span className="nx-cell is-mono" style={{ fontSize: 11 }}>{org.billingRef}</span>  : '—'} />
                    <InfoRow label="Sub ID"        value={<span className="nx-cell is-mono" style={{ fontSize: 11 }}>{org.subscriptionId}</span>} />
                    <InfoRow label="Created"       value={fmtDate(org.subCreatedAt)} />
                  </div>

                  {/* Subscription lifecycle actions */}
                  {!isArchived && (() => {
                    const actions = SUB_STATUS_ACTIONS[org.subscriptionStatus] || [];
                    if (actions.length === 0) return null;
                    return (
                      <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {actions.includes('activate') && (
                          <Btn variant="secondary" size="sm" icon="Check" onClick={async () => {
                            try {
                              await subApi.activate(org.subscriptionId);
                              update({ subscriptionStatus: 'ACTIVE' });
                              data.addAudit({ action: 'activated subscription', target: org.name, cat: 'Subscription', sev: 'info' });
                              toast({ tone: 'success', icon: 'Check', title: 'Subscription Activated' });
                            } catch (err) { toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed', desc: err.message }); }
                          }}>Activate</Btn>
                        )}
                        {actions.includes('reactivate') && (
                          <Btn variant="secondary" size="sm" icon="RefreshCw" onClick={async () => {
                            try {
                              await subApi.reactivate(org.subscriptionId);
                              update({ subscriptionStatus: 'ACTIVE' });
                              data.addAudit({ action: 'reactivated subscription', target: org.name, cat: 'Subscription', sev: 'info' });
                              toast({ tone: 'success', icon: 'RefreshCw', title: 'Subscription Reactivated' });
                            } catch (err) { toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed', desc: err.message }); }
                          }}>Reactivate</Btn>
                        )}
                        {actions.includes('renew') && (
                          <Btn variant="secondary" size="sm" icon="RotateCcw" onClick={async () => {
                            try {
                              // EXPIRED/CANCELLED renew creates a new subscription row with a new ID.
                              // Optimistic patch would leave org.subscriptionId pointing at the old row,
                              // breaking every subsequent lifecycle action — reload to get the new record.
                              await subApi.renew(org.subscriptionId);
                              await data.reload();
                              data.addAudit({ action: 'renewed subscription', target: org.name, cat: 'Subscription', sev: 'info' });
                              toast({ tone: 'success', icon: 'RotateCcw', title: 'Subscription Renewed' });
                            } catch (err) { toast({ tone: 'error', icon: 'AlertCircle', title: 'Renew failed', desc: err.message }); }
                          }}>Renew</Btn>
                        )}
                        {actions.includes('suspend') && (
                          <Btn variant="ghost" size="sm" icon="AlertCircle" onClick={() => setSuspendSubConfirm(true)}>Suspend</Btn>
                        )}
                        {actions.includes('expire') && (
                          <Btn variant="ghost" size="sm" icon="Clock" onClick={() => setExpireSubConfirm(true)}>Mark Expired</Btn>
                        )}
                        {actions.includes('cancel') && (
                          <Btn variant="ghost" size="sm" icon="XCircle" onClick={() => setCancelSubConfirm(true)}>Cancel</Btn>
                        )}
                      </div>
                    );
                  })()}
                </>
              )}
            </div>

            {/* ── License summary card ── */}
            <div className="nx-cc-chart-card" style={{ marginBottom: 16 }}>
              <div className="nx-cc-chart-head">
                <span className="nx-cc-chart-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <NexusIcon name="Key" size={14} /> License Summary
                </span>
                <span className="nx-muted nx-mini">{lics.length} license{lics.length !== 1 ? 's' : ''}</span>
              </div>
              {lics.length === 0 ? (
                <p style={{ fontSize: 13, color: 'var(--nx-text-faint)', marginTop: 10 }}>
                  No licenses purchased yet. Use the Seat License Management section below to add seat-based licenses.
                </p>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10, marginTop: 12 }}>
                  {[
                    { label: 'Seats Purchased', value: userLicPurchased, icon: 'Users',    color: '#125AB0' },
                    { label: 'Seats In Use',    value: userLicUsed,      icon: 'Activity', color: '#0E8C5A' },
                    { label: 'Available',       value: Math.max(0, userLicPurchased - userLicUsed), icon: 'Check', color: '#6E3FBA' },
                    { label: 'License Types',   value: lics.length,       icon: 'Layers',   color: '#C26A1F' },
                  ].map(r => (
                    <div key={r.label} style={{ border: '1px solid var(--nx-border)', borderRadius: 8, padding: '10px 12px', background: 'var(--nx-surface)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 4 }}>
                        <NexusIcon name={r.icon} size={12} style={{ color: r.color }} />
                        <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--nx-text-muted)' }}>{r.label}</span>
                      </div>
                      <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--nx-text)', fontVariantNumeric: 'tabular-nums' }}>{r.value}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ── Change Plan ── */}
            {!isArchived && (
              <div className="nx-cc-chart-card" style={{ marginBottom: 16 }}>
                <div className="nx-cc-chart-head">
                  <span className="nx-cc-chart-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <NexusIcon name="ArrowUpCircle" size={14} /> Change Plan
                  </span>
                  <span className="nx-muted nx-mini">Assign a different plan to this organization</span>
                </div>
                <div className="nx-form" style={{ marginTop: 12 }}>
                {loadingPlans && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 10, marginBottom: 16 }}>
                    {[1,2,3,4].map(i => <div key={i} className="nx-cc-skeleton" style={{ height: 72, borderRadius: 8 }} />)}
                  </div>
                )}
                {!loadingPlans && livePlans.length === 0 && (
                  <p style={{ fontSize: 13, color: 'var(--nx-text-faint)', marginBottom: 16 }}>No active plans available.</p>
                )}
                {!loadingPlans && livePlans.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 10, marginBottom: 16 }}>
                  {livePlans.map(plan => {
                    const isCurrent  = plan.id === org.planId;
                    const isSelected = selectedPlanId === plan.id;
                    return (
                      <div key={plan.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => setSelectedPlanId(plan.id)}
                        onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && setSelectedPlanId(plan.id)}
                        style={{
                          border: `2px solid ${isSelected || (!selectedPlanId && isCurrent) ? 'var(--nx-accent)' : 'var(--nx-border)'}`,
                          borderRadius: 8, padding: 12, cursor: 'pointer',
                          background: isSelected || (!selectedPlanId && isCurrent) ? 'var(--nx-accent-tint,rgba(18,90,176,0.06))' : 'var(--nx-surface)',
                        }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                          <span style={{ fontWeight: 600, fontSize: 14 }}>{plan.name}</span>
                          {isCurrent && <Pill tone="blue">Current</Pill>}
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>
                          {plan.priceMonthly != null ? `${plan.currency || 'INR'} ${plan.priceMonthly.toLocaleString()}/mo` : 'Custom pricing'}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--nx-text-faint)' }}>
                          {plan.maxWorkspaces ? `Up to ${plan.maxWorkspaces} workspaces` : 'Unlimited workspaces'}
                        </div>
                      </div>
                    );
                  })}
                </div>
                )}
                <Btn
                  variant="primary" icon="Check"
                  disabled={!selectedPlanId || selectedPlanId === org.planId || loadingPlans}
                  onClick={() => {
                    if (!org.subscriptionId) {
                      toast({ tone: 'error', icon: 'AlertCircle', title: 'No subscription found — provision one first' });
                      return;
                    }
                    setPlanChangeConfirm(true);
                  }}>
                  Apply Plan Change
                </Btn>
                </div>
              </div>
            )}

            {/* ── Subscription History timeline ── */}
            {(() => {
              const commercialAudit = orgAudit.filter(a => {
                const act = (a.action || '').toLowerCase();
                return (
                  a.cat === 'Subscription' || a.cat === 'License' ||
                  act.includes('plan') || act.includes('subscri') || act.includes('licens') ||
                  act.includes('suspend') || act.includes('expir') || act.includes('cancel') ||
                  act.includes('renew') || act.includes('activat') || act.includes('restor') ||
                  act.includes('archiv')
                );
              });
              if (commercialAudit.length === 0) return null;
              const sevIcon = { info: 'CheckCircle', warning: 'AlertCircle', error: 'XCircle', critical: 'XCircle' };
              const sevColor = { info: 'var(--nx-accent)', warning: '#E08A00', error: 'var(--nx-danger)', critical: 'var(--nx-danger)' };
              return (
                <div className="nx-cc-chart-card">
                  <div className="nx-cc-chart-head">
                    <span className="nx-cc-chart-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <NexusIcon name="Clock" size={14} /> Subscription History
                    </span>
                    <span className="nx-muted nx-mini">{commercialAudit.length} events</span>
                  </div>
                  <div style={{ marginTop: 12, position: 'relative', paddingLeft: 24 }}>
                    <div style={{ position: 'absolute', left: 10, top: 0, bottom: 0, width: 1, background: 'var(--nx-border)' }} />
                    {[...commercialAudit].sort((a, b) => new Date(b.rawDate || 0) - new Date(a.rawDate || 0)).slice(0, 20).map(a => (
                      <div key={a.id} style={{ position: 'relative', marginBottom: 12, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                        <div style={{
                          position: 'absolute', left: -20, width: 20, height: 20, borderRadius: '50%',
                          background: 'var(--nx-surface)', border: '2px solid var(--nx-border)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        }}>
                          <NexusIcon name={sevIcon[a.sev] || 'Circle'} size={10} style={{ color: sevColor[a.sev] || 'var(--nx-text-muted)' }} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--nx-text)' }}>{a.action}</div>
                          <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
                            <span style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>{a.who}</span>
                            <span style={{ fontSize: 11, color: 'var(--nx-text-faint)' }}>{a.time}</span>
                          </div>
                        </div>
                        <Pill tone={a.cat === 'License' ? 'purple' : 'blue'} style={{ flexShrink: 0 }}>{a.cat}</Pill>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* ── Resource Quotas ── */}
            {[userLic?.purchased, org.maxWorkspaces, org.maxAgents, org.maxBots].some(v => v != null) && (
              <div className="nx-cc-chart-card" style={{ marginBottom: 16 }}>
                <div className="nx-cc-chart-head">
                  <span className="nx-cc-chart-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <NexusIcon name="BarChart2" size={14} /> Resource Quotas
                  </span>
                  <span className="nx-muted nx-mini">Active limits provisioned from current plan</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12, marginTop: 14 }}>
                  {[
                    { label: 'Users',      used: org.users,   max: userLic?.purchased ?? null, icon: 'Users',      color: '#125AB0' },
                    { label: 'Workspaces', used: wss.length,  max: org.maxWorkspaces, icon: 'LayoutGrid', color: '#6E3FBA' },
                    { label: 'AI Agents',  used: 0,           max: org.maxAgents,     icon: 'Cpu',        color: '#0E8C5A' },
                    { label: 'Bots',       used: 0,           max: org.maxBots,       icon: 'Bot',        color: '#C26A1F' },
                  ].filter(r => r.max != null).map(r => {
                    const p = pct(r.used, r.max);
                    return (
                      <div key={r.label} style={{ border: '1px solid var(--nx-border)', borderRadius: 8, padding: '12px 14px', background: 'var(--nx-surface)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                          <NexusIcon name={r.icon} size={13} style={{ color: r.color }} />
                          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--nx-text-muted)' }}>{r.label}</span>
                        </div>
                        <div className="nx-cc-lic-track" style={{ marginBottom: 6 }}>
                          <span style={{ width: p + '%', background: p > 90 ? 'var(--nx-danger)' : p > 75 ? '#E08A00' : r.color }} />
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--nx-text-muted)' }}>
                          <span>{r.used} used</span>
                          <span>{r.max - r.used} remaining</span>
                          <span style={{ fontWeight: 700, color: p > 90 ? 'var(--nx-danger)' : 'var(--nx-text)' }}>{p}%</span>
                        </div>
                        <div style={{ marginTop: 4, fontSize: 11, color: 'var(--nx-text-faint)' }}>of {r.max} allocated</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── Seat License Management ── */}
            <div className="nx-cc-chart-card" style={{ marginBottom: 16 }}>
              <div className="nx-cc-chart-head">
                <span className="nx-cc-chart-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <NexusIcon name="Key" size={14} /> Seat License Management
                </span>
                {!isArchived && (
                  <Btn variant="ghost" size="sm" icon="Plus" onClick={() => { setAddLicModal('USER'); setAddLicSeats(10); }}>Add License</Btn>
                )}
              </div>

              {lics.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px 16px', color: 'var(--nx-text-muted)', fontSize: 13 }}>
                  <NexusIcon name="Key" size={20} style={{ opacity: 0.3, marginBottom: 8, display: 'block', margin: '0 auto 8px' }} />
                  No purchased licenses. Click "Add License" to assign seat-based licenses to this organization.
                </div>
              ) : (
                <div style={{ marginTop: 12 }}>
                  {lics.map(l => {
                    const p         = pct(l.used, l.purchased);
                    const available = l.available ?? Math.max(0, (l.purchased ?? 0) - (l.used ?? 0));
                    const rs        = licRenewalStatus(l.renews);
                    return (
                      <div key={l.id} className="nx-cc-lic-row" style={{ flexWrap: 'wrap', gap: 12 }}>
                        <Pill tone={l.type === 'USER' ? 'blue' : l.type === 'BOT' ? 'purple' : l.type === 'AGENT' ? 'green' : 'amber'}>
                          {LIC_TYPE_LABEL[l.type] || l.type}
                        </Pill>
                        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 180, gap: 2 }}>
                          <div className="nx-cc-lic-track">
                            <span style={{ width: p + '%', background: p > 90 ? 'var(--nx-danger)' : p > 75 ? '#E08A00' : 'var(--nx-success)' }} />
                          </div>
                          <span className="nx-muted nx-mini">{l.purchased} purchased · {l.used} used · {available} available</span>
                        </div>
                        <Pill tone={rs.tone}>{rs.label}</Pill>
                        <Pill tone={LIC_STATUS_TONE[l.status] || 'gray'}>{l.status || 'ACTIVE'}</Pill>
                        {!isArchived && (
                          <div style={{ display: 'flex', gap: 4, marginLeft: 'auto' }}>
                            <Btn variant="ghost" size="sm" icon="Edit2"
                              onClick={() => { setEditLicModal(l); setEditLicNew(l.purchased); setEditLicReason(''); }}>
                              Edit
                            </Btn>
                            <Btn variant="ghost" size="sm" icon="RotateCcw" onClick={async () => {
                              try {
                                const updated = await licenseApi.renew(l.id);
                                const newRenews = updated?.renews ? String(updated.renews).split('T')[0] : l.renews;
                                data.update(s => ({ ...s, licenses: s.licenses.map(x => x.id === l.id ? { ...x, renews: newRenews } : x) }));
                                data.addAudit({ action: 'renewed license', target: org.name + ' · ' + l.type, cat: 'License' });
                                toast({ tone: 'success', icon: 'Key', title: 'License Renewed' });
                              } catch (err) { toast({ tone: 'error', icon: 'AlertCircle', title: 'Renew failed', desc: err.message }); }
                            }}>Renew</Btn>
                            <Btn variant="ghost" size="sm" icon="Trash"
                              onClick={() => setDeleteLicConfirm(l)}>Delete</Btn>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

          </div>
          )
        )}

        {/* ── WORKSPACES ──────────────────────────────────────── */}
        {tab === 'workspaces' && (
          <div>
            <div className="nx-cc-stat-grid" style={{ gridTemplateColumns: 'repeat(3,1fr)', marginBottom: 16 }}>
              <StatCard label="Workspaces"    value={org.maxWorkspaces != null ? wss.length + ' / ' + org.maxWorkspaces : wss.length} icon="LayoutGrid" color="#125AB0" />
              <StatCard label="Total Members" value={wss.reduce((s, w) => s + w.members, 0)} icon="Users"   color="#0E8C5A" />
              <StatCard label="Active"        value={wss.filter(w => (w.status || '').toUpperCase() === 'ACTIVE').length} icon="Check"   color="#6E3FBA" />
            </div>

            {wss.length === 0
              ? <div className="nx-empty"><NexusIcon name="LayoutGrid" size={20} /><span>No workspaces in this organization yet.</span></div>
              : <>
                  <div className="nx-table-wrap"><div className="nx-table-scroll"><table className="nx-table">
                    <thead><tr>
                      <Th icon="LayoutGrid">Workspace</Th>
                      <Th icon="Users">Members</Th>
                      <Th icon="Layers">Modules</Th>
                      <Th icon="Activity">Status</Th>
                      <Th icon="Calendar">Created</Th>
                    </tr></thead>
                    <tbody>{wss.map(w => (
                      <tr key={w.id} className="nx-tr"
                        style={{ cursor: 'pointer', background: selectedWsId === w.id ? 'var(--nx-accent-tint, rgba(18,90,176,0.06))' : undefined }}
                        onClick={() => setSelectedWsId(prev => prev === w.id ? null : w.id)}>
                        <td>
                          <span className="nx-row-name">
                            <span className="nx-domain-mark" style={{ width: 22, height: 22, fontSize: 10 }}>
                              {w.name.split(' ').map(x => x[0]).slice(0, 2).join('')}
                            </span>
                            <span className="nx-row-name-text">{w.name}</span>
                          </span>
                        </td>
                        <td><span className="nx-cell is-mono">{w.members}</span></td>
                        <td><span className="nx-muted nx-mini">{(w.modules || []).join(' · ') || '—'}</span></td>
                        <td><Pill tone={statusTone(w.status)}>{w.status}</Pill></td>
                        <td><span className="nx-muted nx-mini">{w.created}</span></td>
                      </tr>
                    ))}</tbody>
                  </table></div></div>

                  {/* Workspace detail panel */}
                  {selectedWsId && (() => {
                    const ws = wss.find(w => w.id === selectedWsId);
                    if (!ws) return null;
                    const wsMs = workspaceMembers(data.state, ws.id);
                    return (
                      <div style={{ marginTop: 14, border: '1px solid var(--nx-border)', borderRadius: 10, background: 'var(--nx-surface)', overflow: 'hidden' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 16px', borderBottom: '1px solid var(--nx-border)', background: 'var(--nx-surface-alt)' }}>
                          <NexusIcon name="LayoutGrid" size={14} />
                          <span style={{ fontWeight: 700, fontSize: 13 }}>{ws.name}</span>
                          <Pill tone={statusTone(ws.status)}>{ws.status}</Pill>
                          <span className="nx-muted nx-mini">Created {ws.created}</span>
                          {ws.modules?.length > 0 && <span className="nx-muted nx-mini">Modules: {ws.modules.join(', ')}</span>}
                          <button style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--nx-text-muted)', padding: 4 }}
                            onClick={() => setSelectedWsId(null)}>
                            <NexusIcon name="X" size={14} />
                          </button>
                        </div>
                        <div style={{ padding: 16 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 10 }}>
                            Members ({wsMs.length})
                          </div>
                          {wsMs.length === 0
                            ? <div className="nx-empty" style={{ padding: '12px 0' }}><span style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>No members in this workspace.</span></div>
                            : <table className="nx-table">
                                <thead><tr>
                                  <Th icon="User">Name</Th>
                                  <Th icon="Mail">Email</Th>
                                  <Th icon="Shield">Org Role</Th>
                                  <Th icon="Layers">Workspace Role</Th>
                                  <Th icon="Activity">Status</Th>
                                </tr></thead>
                                <tbody>{wsMs.map(wm => (
                                  <tr key={wm.id} className="nx-tr">
                                    <td><span className="nx-row-name-text">{wm.member?.name || '—'}</span></td>
                                    <td><span className="nx-muted">{wm.member?.email || '—'}</span></td>
                                    <td><Pill tone={wm.member?.orgRole === 'ORG_ADMIN' ? 'purple' : 'gray'}>{wm.member?.orgRole === 'ORG_ADMIN' ? 'Org Admin' : 'Member'}</Pill></td>
                                    <td><Pill tone={wm.wsRole === 'WORKSPACE_ADMIN' ? 'blue' : 'gray'}>{wm.wsRole === 'WORKSPACE_ADMIN' ? 'Workspace Admin' : 'Workspace User'}</Pill></td>
                                    <td><Pill tone={statusTone(wm.status)}>{wm.status}</Pill></td>
                                  </tr>
                                ))}</tbody>
                              </table>
                          }
                        </div>
                      </div>
                    );
                  })()}
                </>
            }
          </div>
        )}

        {/* ── USERS ───────────────────────────────────────────── */}
        {tab === 'users' && (
          <div>
            <div className="nx-cc-stat-grid" style={{ gridTemplateColumns: 'repeat(4,1fr)', marginBottom: 16 }}>
              <StatCard label="Total Members"  value={members.length}                                       icon="Users"       color="#125AB0" />
              <StatCard label="Active"         value={members.filter(m => (m.status || '').toUpperCase() === 'ACTIVE').length}    icon="Check"       color="#0E8C5A" />
              <StatCard label="Pending"        value={members.filter(m => (m.status || '').toUpperCase() === 'PENDING').length}   icon="Clock"       color="#E08A00" />
              <StatCard label="Suspended"      value={members.filter(m => (m.status || '').toUpperCase() === 'SUSPENDED').length} icon="AlertCircle" color="#C26A1F" />
            </div>

            {members.length === 0
              ? <div className="nx-empty"><NexusIcon name="Users" size={20} /><span>No members in this organization yet.</span></div>
              : <div className="nx-table-wrap"><div className="nx-table-scroll"><table className="nx-table">
                  <thead><tr>
                    <Th icon="User">Name</Th>
                    <Th icon="Mail">Email</Th>
                    <Th icon="Shield">Role</Th>
                    <Th icon="LayoutGrid">Workspaces</Th>
                    <Th icon="Activity">Status</Th>
                  </tr></thead>
                  <tbody>{members.map(m => {
                    const wsCount = data.state.memberships
                      ? data.state.memberships.filter(mb => mb.memberId === m.id || mb.memberId === m.userId).length
                      : 0;
                    return (
                      <tr key={m.id} className="nx-tr" style={{ cursor: 'pointer' }} onClick={() => setSelectedMember(m)}>
                        <td>
                          <span className="nx-row-name">
                            <span className="nx-domain-mark" style={{ width: 24, height: 24, fontSize: 10, background: m.tone || '#125AB0' }}>
                              {(m.name || 'U').split(' ').map(w => w[0]).slice(0, 2).join('')}
                            </span>
                            <span className="nx-row-name-text">{m.name}</span>
                          </span>
                        </td>
                        <td><span className="nx-muted">{m.email}</span></td>
                        <td><Pill tone={m.orgRole === 'ORG_ADMIN' ? 'purple' : 'gray'}>{m.orgRole === 'ORG_ADMIN' ? 'Org Admin' : 'Member'}</Pill></td>
                        <td><span className="nx-muted nx-mini">{wsCount > 0 ? wsCount + ' workspace' + (wsCount !== 1 ? 's' : '') : '—'}</span></td>
                        <td><Pill tone={statusTone(m.status)}>{m.status}</Pill></td>
                      </tr>
                    );
                  })}</tbody>
                </table></div></div>
            }
          </div>
        )}

        {/* ── MEMBER DETAIL SLIDE-OVER ──────────────────────── */}
        {selectedMember && (
          <>
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.28)', zIndex: 119 }} onClick={() => setSelectedMember(null)} />
            <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 540, background: 'var(--nx-bg)', borderLeft: '1px solid var(--nx-border)', zIndex: 120, display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)', overflowY: 'auto' }}>
              {/* Header */}
              <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--nx-border)', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
                <div className="nx-domain-mark" style={{ background: selectedMember.tone ? selectedMember.tone + '22' : '#125AB018', color: selectedMember.tone || '#125AB0', width: 36, height: 36, fontSize: 14 }}>
                  {(selectedMember.name || 'U').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--nx-text)' }}>{selectedMember.name}</div>
                  <div className="nx-muted nx-mini">{selectedMember.email}</div>
                </div>
                <Pill tone={statusTone(selectedMember.status)}>{selectedMember.status}</Pill>
                <Pill tone={selectedMember.orgRole === 'ORG_ADMIN' ? 'purple' : 'gray'}>{selectedMember.orgRole === 'ORG_ADMIN' ? 'Org Admin' : 'Member'}</Pill>
                <button onClick={() => setSelectedMember(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--nx-text-muted)', padding: 4 }}><NexusIcon name="X" size={16} /></button>
              </div>
              {/* Quick actions */}
              {!isArchived && (
                <div style={{ padding: '10px 20px', borderBottom: '1px solid var(--nx-border)', display: 'flex', gap: 8, flexWrap: 'wrap', flexShrink: 0 }}>
                  {(selectedMember.status || '').toUpperCase() === 'ACTIVE' && (
                    <Btn variant="ghost" size="sm" icon="AlertCircle" onClick={async () => {
                      try {
                        await orgApi.suspendMember(org.id, selectedMember.id);
                        data.update(s => ({ ...s, members: s.members.map(x => x.id === selectedMember.id ? { ...x, status: 'SUSPENDED' } : x) }));
                        setSelectedMember(m => ({ ...m, status: 'SUSPENDED' }));
                        toast({ tone: 'warning', icon: 'AlertCircle', title: 'Member Suspended' });
                      } catch (err) { toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed', desc: err.message }); }
                    }}>Suspend</Btn>
                  )}
                  {(selectedMember.status || '').toUpperCase() === 'SUSPENDED' && (
                    <Btn variant="ghost" size="sm" icon="Check" onClick={async () => {
                      try {
                        await orgApi.activateMember(org.id, selectedMember.id);
                        data.update(s => ({ ...s, members: s.members.map(x => x.id === selectedMember.id ? { ...x, status: 'ACTIVE' } : x) }));
                        setSelectedMember(m => ({ ...m, status: 'ACTIVE' }));
                        toast({ tone: 'success', icon: 'Check', title: 'Member Activated' });
                      } catch (err) { toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed', desc: err.message }); }
                    }}>Activate</Btn>
                  )}
                  <Btn variant="ghost" size="sm" icon="Key" onClick={async () => {
                    try { await authApi.forgotPassword(selectedMember.email); toast({ tone: 'success', icon: 'Key', title: 'Reset link sent', desc: selectedMember.email }); }
                    catch (err) { toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed', desc: err.message }); }
                  }}>Reset Password</Btn>
                  <Btn variant="ghost" size="sm" icon="Trash"
                    onClick={() => setRemoveMemberConfirm(selectedMember)}>Remove</Btn>
                </div>
              )}
              {/* Detail fields */}
              <div style={{ padding: 20, flex: 1 }}>
                <div className="nx-cc-stat-grid" style={{ gridTemplateColumns: 'repeat(2,1fr)', marginBottom: 16 }}>
                  <StatCard label="Status" value={selectedMember.status}           icon="Activity"    color={(selectedMember.status || '').toUpperCase() === 'ACTIVE' ? '#0E8C5A' : '#C26A1F'} />
                  <StatCard label="Role"   value={selectedMember.orgRole || 'Member'} icon="ShieldCheck" color="#6E3FBA" />
                </div>
                <div className="nx-form">
                  {[
                    ['Name',     selectedMember.name],
                    ['Email',    selectedMember.email],
                    ['Org Role', selectedMember.orgRole === 'ORG_ADMIN' ? 'Org Admin' : 'Member'],
                    ['Status',   selectedMember.status],
                    ['Invited',  selectedMember.invitedAt ? new Date(selectedMember.invitedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'],
                    ['Joined',   selectedMember.joinedAt  ? new Date(selectedMember.joinedAt).toLocaleDateString('en-IN',  { day: 'numeric', month: 'short', year: 'numeric' }) : '—'],
                  ].map(([label, value]) => (
                    <div key={label} className="nx-form-row"><label>{label}</label><span>{value}</span></div>
                  ))}
                </div>
                <div style={{ marginTop: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Workspace Memberships</div>
                  {(() => {
                    const wsMs = data.state.memberships
                      ? data.state.memberships.filter(mb => mb.memberId === selectedMember.id || mb.memberId === selectedMember.userId)
                      : [];
                    return wsMs.length === 0
                      ? <span className="nx-muted nx-mini">Not a member of any workspace in this org.</span>
                      : <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {wsMs.map(mb => <Pill key={mb.id} tone="blue">{mb.workspaceName || mb.workspaceId || mb.id}</Pill>)}
                        </div>;
                  })()}
                </div>
              </div>
            </div>
          </>
        )}

        {/* ── AUDIT LOGS ──────────────────────────────────────── */}
        {tab === 'audit' && (
          orgAudit.length === 0
            ? <div className="nx-empty"><NexusIcon name="Activity" size={20} /><span>No audit events for this organization yet.</span></div>
            : <div className="nx-table-wrap"><div className="nx-table-scroll"><table className="nx-table">
                <thead><tr>
                  <Th icon="User">Actor</Th>
                  <Th icon="Activity">Action</Th>
                  <Th icon="Layers">Category</Th>
                  <Th icon="AlertCircle">Severity</Th>
                  <Th icon="Clock">When</Th>
                </tr></thead>
                <tbody>{orgAudit.map(a => (
                  <tr key={a.id} className="nx-tr">
                    <td>
                      <span className="nx-row-name">
                        <span className="nx-domain-mark" style={{ width: 22, height: 22, fontSize: 10 }}>
                          {(a.who || 'S')[0]}
                        </span>
                        <span className="nx-row-name-text">{a.who}</span>
                      </span>
                    </td>
                    <td><Pill tone={a.sev === 'warning' ? 'amber' : 'blue'}>{a.action}</Pill></td>
                    <td><span className="nx-muted">{a.cat}</span></td>
                    <td><Pill tone={{ info: 'blue', warning: 'amber', error: 'red', critical: 'red' }[a.sev] || 'gray'}>{a.sev || 'info'}</Pill></td>
                    <td><span className="nx-muted">{a.time}</span></td>
                  </tr>
                ))}</tbody>
              </table></div></div>
        )}

        {/* ── SETTINGS (org config only — no license fields) ──── */}
        {tab === 'settings' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 28, maxWidth: 580 }}>

            <div className="nx-form">
              <h3 className="nx-panel-title" style={{ marginBottom: 12 }}>Organization Information</h3>
              <div className="nx-form-row">
                <label>Organization Name</label>
                <input className="nx-input" defaultValue={org.name} disabled={isArchived}
                  onBlur={async e => { if (!isArchived) { const v = e.target.value; update({ name: v }); try { await orgApi.update(org.id, { name: v }); toast({ tone: 'success', icon: 'Check', title: 'Name Updated' }); } catch { toast({ tone: 'warning', icon: 'AlertCircle', title: 'Sync failed' }); } } }} />
              </div>
              <div className="nx-form-row">
                <label>Industry</label>
                <input className="nx-input" defaultValue={org.industry} disabled={isArchived}
                  onBlur={e => { if (!isArchived) { update({ industry: e.target.value }); syncField('industry', e.target.value); } }} />
              </div>
              <div className="nx-form-row">
                <label>Country</label>
                <select className="nx-input" value={org.country} disabled={isArchived}
                  onChange={e => { if (!isArchived) { update({ country: e.target.value }); syncField('country', e.target.value); } }}>
                  <option value="">— Select —</option>
                  {[
                    { label: 'India',       value: 'IN' },
                    { label: 'UAE',         value: 'AE' },
                    { label: 'Singapore',   value: 'SG' },
                    { label: 'USA',         value: 'US' },
                    { label: 'UK',          value: 'GB' },
                    { label: 'Australia',   value: 'AU' },
                    { label: 'Canada',      value: 'CA' },
                  ].map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div className="nx-form-row">
                <label>Timezone</label>
                <select className="nx-input" value={org.timezone} disabled={isArchived}
                  onChange={e => { if (!isArchived) { update({ timezone: e.target.value }); syncField('timezone', e.target.value); } }}>
                  <option value="">— Select —</option>
                  {[
                    { label: 'IST — Asia/Kolkata',        value: 'Asia/Kolkata'        },
                    { label: 'GST — Asia/Dubai',          value: 'Asia/Dubai'          },
                    { label: 'SGT — Asia/Singapore',      value: 'Asia/Singapore'      },
                    { label: 'EST — America/New_York',    value: 'America/New_York'    },
                    { label: 'PST — America/Los_Angeles', value: 'America/Los_Angeles' },
                    { label: 'GMT — Europe/London',       value: 'Europe/London'       },
                    { label: 'AEST — Australia/Sydney',   value: 'Australia/Sydney'    },
                  ].map(z => <option key={z.value} value={z.value}>{z.label}</option>)}
                </select>
              </div>
            </div>

            <div className="nx-form">
              <h3 className="nx-panel-title" style={{ marginBottom: 12 }}>Branding</h3>
              {branding
                ? <>
                    <InfoRow label="Primary Color" value={
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ width: 14, height: 14, borderRadius: 3, background: branding.primary, display: 'inline-block', border: '1px solid var(--nx-border)' }} />
                        {branding.primary}
                      </span>
                    } />
                    <InfoRow label="Logo Text"     value={branding.logoText} />
                    <InfoRow label="Domain"        value={branding.domain || '—'} />
                    <InfoRow label="Domain Status" value={<Pill tone={branding.domainStatus === 'VERIFIED' ? 'green' : 'amber'}>{branding.domainStatus}</Pill>} />
                    <div className="nx-form-row">
                      <label />
                      <span className="nx-muted nx-mini">Managed by the Org Admin in their Organization Console.</span>
                    </div>
                  </>
                : <span className="nx-muted nx-mini">No branding configured. Managed by the Org Admin in their console.</span>
              }
            </div>

            <div className="nx-form">
              <h3 className="nx-panel-title" style={{ marginBottom: 12 }}>Organization Admin</h3>
              {org.ownerId ? (
                <>
                  <InfoRow label="Name"   value={org.owner}      />
                  <InfoRow label="Email"  value={org.ownerEmail} />
                  <InfoRow label="Status" value={<Pill tone="green">Assigned</Pill>} />
                  {inviteUrl && (
                    <div className="nx-form-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 6 }}>
                      <label>Invite Link</label>
                      <div style={{ display: 'flex', gap: 8, width: '100%', alignItems: 'center' }}>
                        <input className="nx-input" readOnly value={inviteUrl}
                          style={{ flex: 1, fontSize: 11, fontFamily: 'monospace' }}
                          onClick={e => e.target.select()} />
                        <Btn variant="ghost" size="sm" icon="Copy"
                          onClick={() => { navigator.clipboard.writeText(inviteUrl); toast({ tone: 'success', icon: 'Copy', title: 'Link copied' }); }}>
                          Copy
                        </Btn>
                      </div>
                      <span className="nx-muted nx-mini">Share with the admin. Valid for 7 days.</span>
                    </div>
                  )}
                </>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'flex-start' }}>
                  <span className="nx-muted">No admin assigned to this organization.</span>
                  {!isArchived && (
                    <Btn variant="secondary" icon="UserPlus" onClick={() => setProvisionModal(true)}>Create Org Admin</Btn>
                  )}
                </div>
              )}
            </div>

          </div>
        )}

      </div>

      {/* ── Subscription action confirms ───────────────────────── */}
      {suspendSubConfirm && (
        <TenancyModal title="Suspend Subscription?" icon="AlertCircle" danger onClose={() => setSuspendSubConfirm(false)}>
          <p className="nx-confirm-desc">
            Suspending the subscription will restrict the organization's access to plan features.
            The subscription can be reactivated at any time.
          </p>
          <div className="nx-confirm-actions">
            <Btn variant="ghost" onClick={() => setSuspendSubConfirm(false)}>Cancel</Btn>
            <Btn variant="danger" icon="AlertCircle" onClick={async () => {
              try {
                await subApi.suspend(org.subscriptionId);
                update({ subscriptionStatus: 'SUSPENDED' });
                data.addAudit({ action: 'suspended subscription', target: org.name, cat: 'Subscription', sev: 'warning' });
                toast({ tone: 'warning', icon: 'AlertCircle', title: 'Subscription Suspended' });
                setSuspendSubConfirm(false);
              } catch (err) { toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed', desc: err.message }); }
            }}>Suspend Subscription</Btn>
          </div>
        </TenancyModal>
      )}

      {expireSubConfirm && (
        <TenancyModal title="Mark Subscription Expired?" icon="Clock" danger onClose={() => setExpireSubConfirm(false)}>
          <p className="nx-confirm-desc">
            Marking as expired is a terminal action that cannot be undone automatically.
            You will need to renew to create a new active subscription.
          </p>
          <div className="nx-confirm-actions">
            <Btn variant="ghost" onClick={() => setExpireSubConfirm(false)}>Cancel</Btn>
            <Btn variant="danger" icon="Clock" onClick={async () => {
              try {
                await subApi.expire(org.subscriptionId);
                update({ subscriptionStatus: 'EXPIRED' });
                data.addAudit({ action: 'expired subscription', target: org.name, cat: 'Subscription', sev: 'warning' });
                toast({ tone: 'warning', icon: 'Clock', title: 'Subscription Expired' });
                setExpireSubConfirm(false);
              } catch (err) { toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed', desc: err.message }); }
            }}>Mark Expired</Btn>
          </div>
        </TenancyModal>
      )}

      {cancelSubConfirm && (
        <TenancyModal title="Cancel Subscription?" icon="XCircle" danger onClose={() => setCancelSubConfirm(false)}>
          <p className="nx-confirm-desc">
            Cancelling is a permanent action. The subscription will be terminated and cannot be reactivated.
            You will need to renew to create a new subscription.
          </p>
          <div className="nx-confirm-actions">
            <Btn variant="ghost" onClick={() => setCancelSubConfirm(false)}>Cancel</Btn>
            <Btn variant="danger" icon="XCircle" onClick={async () => {
              try {
                await subApi.cancel(org.subscriptionId);
                update({ subscriptionStatus: 'CANCELLED' });
                data.addAudit({ action: 'cancelled subscription', target: org.name, cat: 'Subscription', sev: 'warning' });
                toast({ tone: 'warning', icon: 'XCircle', title: 'Subscription Cancelled' });
                setCancelSubConfirm(false);
              } catch (err) { toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed', desc: err.message }); }
            }}>Cancel Subscription</Btn>
          </div>
        </TenancyModal>
      )}

      {/* ── Restore confirm ─────────────────────────────────────── */}
      {restoreConfirm && (
        <TenancyModal title={'Restore ' + org.name + '?'} icon="RotateCcw" onClose={() => setRestoreConfirm(false)}>
          <p className="nx-confirm-desc">
            Restoring will move this organization from Archived to Suspended. From there, you can Activate it
            to resume normal operations. Workspaces will remain in their archived state until the org is activated.
          </p>
          <div className="nx-confirm-actions">
            <Btn variant="ghost" onClick={() => setRestoreConfirm(false)}>Cancel</Btn>
            <Btn variant="secondary" icon="RotateCcw" onClick={handleRestore}>Restore Organization</Btn>
          </div>
        </TenancyModal>
      )}

      {/* ── Archive confirm ────────────────────────────────────── */}
      {archiveConfirm && (
        <TenancyModal title={'Archive ' + org.name + '?'} icon="Archive" danger onClose={() => setArchiveConfirm(false)}>
          <p className="nx-confirm-desc">
            Archiving makes this organization read-only. The admin cannot log in. Workspaces are disabled.
            Super Admin or Platform Admin can restore the organization from this page.
          </p>
          <div className="nx-confirm-actions">
            <Btn variant="ghost" onClick={() => setArchiveConfirm(false)}>Cancel</Btn>
            <Btn variant="danger" icon="Archive" onClick={handleArchive}>Archive Organization</Btn>
          </div>
        </TenancyModal>
      )}

      {/* ── Create Org Admin modal ─────────────────────────────── */}
      {provisionModal && (
        <TenancyModal
          title="Create Organization Admin"
          icon="UserPlus"
          dialog
          onClose={() => { setProvisionModal(false); setProvisionForm(EMPTY_PROVISION); }}
          footer={<>
            <Btn variant="ghost" onClick={() => { setProvisionModal(false); setProvisionForm(EMPTY_PROVISION); }}>Cancel</Btn>
            <Btn
              variant="primary"
              icon="UserCheck"
              disabled={provisionBusy || !provisionForm.firstName || !provisionForm.lastName || !provisionForm.email}
              onClick={handleProvision}>
              {provisionBusy ? 'Creating…' : 'Create & Generate Invite'}
            </Btn>
          </>}>
          <div className="nx-form">
            {[
              ['First Name', 'firstName', 'text',  'First name'],
              ['Last Name',  'lastName',  'text',  'Last name'],
              ['Email',      'email',     'email', 'admin@company.com'],
              ['Phone',      'phone',     'tel',   '+91 9876543210'],
            ].map(([label, field, type, placeholder]) => (
              <div key={field} className="nx-form-row">
                <label>{label}{field !== 'phone' && <span style={{ color: 'var(--nx-danger)' }}> *</span>}</label>
                <input className="nx-input" type={type} value={provisionForm[field]} placeholder={placeholder}
                  onChange={e => setProvisionForm(f => ({ ...f, [field]: e.target.value }))} />
              </div>
            ))}
            <p className="nx-muted nx-mini">
              An invite link will be generated. The admin sets their own password when accepting.
            </p>
          </div>
        </TenancyModal>
      )}
      {/* ── Suspend Organization confirm ────────────────────────── */}
      {suspendOrgConfirm && (
        <TenancyModal title={'Suspend ' + org.name + '?'} icon="AlertCircle" danger onClose={() => setSuspendOrgConfirm(false)}>
          <p className="nx-confirm-desc">
            Suspending this organization will block all users from logging in.
            The organization can be reactivated at any time from this page.
          </p>
          <div className="nx-confirm-actions">
            <Btn variant="ghost" onClick={() => setSuspendOrgConfirm(false)}>Cancel</Btn>
            <Btn variant="danger" icon="AlertCircle" onClick={async () => {
              try {
                await orgApi.suspend(org.id);
                update({ status: 'SUSPENDED' });
                data.addAudit({ action: 'suspended org', target: org.name, cat: 'Org', sev: 'warning' });
                toast({ tone: 'warning', icon: 'AlertCircle', title: 'Organization Suspended' });
                setSuspendOrgConfirm(false);
              } catch (err) { toast({ tone: 'error', icon: 'AlertCircle', title: 'Action failed', desc: err.message }); }
            }}>Suspend Organization</Btn>
          </div>
        </TenancyModal>
      )}

      {/* ── Plan change confirm ─────────────────────────────────── */}
      {planChangeConfirm && (() => {
        const toPlan = livePlans.find(p => p.id === selectedPlanId);
        return (
          <TenancyModal title="Confirm Plan Change" icon="CreditCard" onClose={() => setPlanChangeConfirm(false)}>
            <p className="nx-confirm-desc">
              Change <strong>{org.name}</strong>'s plan from <strong>{org.plan || 'No Plan'}</strong> to <strong>{toPlan?.name || selectedPlanId}</strong>?
              This will take effect immediately on the active subscription.
            </p>
            <div className="nx-confirm-actions">
              <Btn variant="ghost" onClick={() => setPlanChangeConfirm(false)}>Cancel</Btn>
              <Btn variant="primary" icon="Check" onClick={async () => {
                try {
                  await subApi.update(org.subscriptionId, { planId: selectedPlanId });
                  update({
                    planId:                   selectedPlanId,
                    plan:                     toPlan?.name                   ?? org.plan,
                    planTier:                 toPlan?.tier                   ?? org.planTier,
                    planDisplayName:          toPlan?.displayName            ?? org.planDisplayName,
                    planDescription:          toPlan?.description            ?? org.planDescription,
                    planStatus:               toPlan?.status                 ?? org.planStatus,
                    planVersion:              toPlan?.version                ?? org.planVersion,
                    planBrandingAllowed:      toPlan?.brandingAllowed        ?? org.planBrandingAllowed,
                    planCustomDomainAllowed:  toPlan?.customDomainAllowed    ?? org.planCustomDomainAllowed,
                    planAiEnabled:            toPlan?.aiEnabled              ?? org.planAiEnabled,
                    planAnalyticsEnabled:     toPlan?.analyticsEnabled       ?? org.planAnalyticsEnabled,
                    // Use ternary (not ??) so null (unlimited) propagates from the new plan.
                    // ?? treats null as nullish and falls back to the old value, showing wrong limits.
                    planMaxStorageGb:         toPlan ? toPlan.maxStorageGb          : org.planMaxStorageGb,
                    planMaxAgents:            toPlan ? toPlan.maxAgents             : org.planMaxAgents,
                    planMaxBots:              toPlan ? toPlan.maxBots               : org.planMaxBots,
                    planMaxApiCallsPerDay:    toPlan ? toPlan.maxApiCallsPerDay      : org.planMaxApiCallsPerDay,
                    planMaxMessagesPerMonth:  toPlan ? toPlan.maxMessagesPerMonth    : org.planMaxMessagesPerMonth,
                    planMaxWorkspaces:        toPlan ? toPlan.maxWorkspaces          : org.planMaxWorkspaces,
                    planFeatureKeys:          Array.isArray(toPlan?.featureKeys) ? toPlan.featureKeys : org.planFeatureKeys,
                    currency:                 toPlan?.currency               ?? org.currency,
                    // Sync the org-level runtime quota fields that syncOrgPlanQuotas writes on the backend.
                    // Without these, Resource Quotas card keeps showing stale limits after a plan change.
                    // Use ternary (not ??) so null (unlimited) is preserved — toPlan is always defined here.
                    maxWorkspaces: toPlan ? toPlan.maxWorkspaces : org.maxWorkspaces,
                    maxAgents:     toPlan ? toPlan.maxAgents     : org.maxAgents,
                    maxBots:       toPlan ? toPlan.maxBots       : org.maxBots,
                  });
                  data.addAudit({ action: 'changed plan to ' + (toPlan?.name || selectedPlanId), target: org.name, cat: 'Subscription', sev: 'info' });
                  toast({ tone: 'success', icon: 'Check', title: 'Plan Updated', desc: 'Now on ' + (toPlan?.name || selectedPlanId) });
                  setSelectedPlanId(null);
                  setPlanChangeConfirm(false);
                } catch (err) { toast({ tone: 'error', icon: 'AlertCircle', title: 'Plan change failed', desc: err.message }); }
              }}>Confirm Plan Change</Btn>
            </div>
          </TenancyModal>
        );
      })()}

      {/* ── Remove Member confirm ───────────────────────────────── */}
      {removeMemberConfirm && (
        <TenancyModal title="Remove Member?" icon="Trash" danger onClose={() => setRemoveMemberConfirm(null)}>
          <p className="nx-confirm-desc">
            Remove <strong>{removeMemberConfirm.name}</strong> ({removeMemberConfirm.email}) from this organization?
            This will revoke their access to all workspaces. This action cannot be undone.
          </p>
          <div className="nx-confirm-actions">
            <Btn variant="ghost" onClick={() => setRemoveMemberConfirm(null)}>Cancel</Btn>
            <Btn variant="danger" icon="Trash" onClick={async () => {
              try {
                await orgApi.removeMember(org.id, removeMemberConfirm.id);
                data.update(s => ({ ...s, members: s.members.filter(x => x.id !== removeMemberConfirm.id) }));
                data.addAudit({ action: 'removed member', target: removeMemberConfirm.name, cat: 'Org', sev: 'warning' });
                toast({ tone: 'warning', icon: 'Trash', title: 'Member Removed' });
                if (selectedMember?.id === removeMemberConfirm.id) setSelectedMember(null);
                setRemoveMemberConfirm(null);
              } catch (err) { toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed', desc: err.message }); }
            }}>Remove Member</Btn>
          </div>
        </TenancyModal>
      )}

      {/* ── Edit License modal ──────────────────────────────────── */}
      {editLicModal && (
        <TenancyModal title="Edit License" icon="Edit2" dialog onClose={() => setEditLicModal(null)}
          footer={<>
            <Btn variant="ghost" onClick={() => setEditLicModal(null)}>Cancel</Btn>
            <Btn
              variant="primary" icon="Check"
              disabled={editLicNew === editLicModal.purchased || editLicNew < editLicModal.used}
              onClick={async () => {
                try {
                  const payload = { allotted: editLicNew };
                  if (editLicReason) payload.reason = editLicReason;
                  await licenseApi.update(editLicModal.id, payload);
                  const prev = editLicModal.purchased;
                  data.update(s => ({ ...s, licenses: s.licenses.map(x =>
                    x.id === editLicModal.id
                      ? { ...x, purchased: editLicNew, available: Math.max(0, editLicNew - x.used), reason: editLicReason || x.reason }
                      : x,
                  ) }));
                  data.addAudit({ action: editLicModal.type + ' license updated: ' + prev + ' → ' + editLicNew, target: org.name, cat: 'License', sev: 'info' });
                  toast({ tone: 'success', icon: 'Key', title: 'License Updated', desc: editLicModal.type + ': ' + prev + ' → ' + editLicNew });
                  setEditLicModal(null);
                } catch (err) { toast({ tone: 'error', icon: 'AlertCircle', title: 'Update failed', desc: err.message }); }
              }}>
              Save Changes
            </Btn>
          </>}>
          <div className="nx-form">
            <div className="nx-form-row">
              <label>License Type</label>
              <input className="nx-input" readOnly value={LIC_TYPE_LABEL[editLicModal.type] || editLicModal.type}
                style={{ background: 'var(--nx-surface-alt)', cursor: 'default' }} />
            </div>
            <div className="nx-form-row">
              <label>Current Quantity</label>
              <input className="nx-input" readOnly value={editLicModal.purchased}
                style={{ background: 'var(--nx-surface-alt)', cursor: 'default', fontVariantNumeric: 'tabular-nums' }} />
            </div>
            <div className="nx-form-row">
              <label>New Quantity</label>
              <input
                type="number" min={editLicModal.used} step={1}
                className="nx-input"
                value={editLicNew}
                onChange={e => setEditLicNew(Math.max(editLicModal.used, Number(e.target.value)))} />
            </div>
            <div className="nx-form-row">
              <label>Difference</label>
              <input className="nx-input" readOnly
                value={(editLicNew > editLicModal.purchased ? '+' : '') + (editLicNew - editLicModal.purchased)}
                style={{
                  background: 'var(--nx-surface-alt)', cursor: 'default',
                  fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                  color: editLicNew > editLicModal.purchased ? '#0E8C5A' : editLicNew < editLicModal.purchased ? 'var(--nx-danger)' : 'var(--nx-text-muted)',
                }} />
            </div>
            <div className="nx-form-row">
              <label>Reason</label>
              <input
                type="text" className="nx-input"
                placeholder="Optional — e.g. contract expansion"
                value={editLicReason}
                onChange={e => setEditLicReason(e.target.value)} />
            </div>
            {editLicNew < editLicModal.used && (
              <p style={{ fontSize: 12, color: 'var(--nx-danger)', margin: 0 }}>
                Cannot set below {editLicModal.used} — currently in use.
              </p>
            )}
          </div>
        </TenancyModal>
      )}

      {/* ── Delete License confirm ───────────────────────────────── */}
      {deleteLicConfirm && (
        <TenancyModal title="Delete License?" icon="Trash" danger onClose={() => setDeleteLicConfirm(null)}>
          <p className="nx-confirm-desc">
            Delete the <strong>{LIC_TYPE_LABEL[deleteLicConfirm.type] || deleteLicConfirm.type}</strong> license
            ({deleteLicConfirm.purchased} purchased, {deleteLicConfirm.used} in use)?
            This action cannot be undone.
          </p>
          {deleteLicConfirm.used > 0 && (
            <p style={{ fontSize: 13, color: 'var(--nx-danger)', marginTop: 8, fontWeight: 500 }}>
              Warning: {deleteLicConfirm.used} seat{deleteLicConfirm.used !== 1 ? 's are' : ' is'} currently in use.
            </p>
          )}
          <div className="nx-confirm-actions">
            <Btn variant="ghost" onClick={() => setDeleteLicConfirm(null)}>Cancel</Btn>
            <Btn variant="danger" icon="Trash" onClick={async () => {
              try {
                await licenseApi.remove(deleteLicConfirm.id);
                data.update(s => ({ ...s, licenses: s.licenses.filter(x => x.id !== deleteLicConfirm.id) }));
                data.addAudit({ action: deleteLicConfirm.type + ' license deleted', target: org.name, cat: 'License', sev: 'warning' });
                toast({ tone: 'warning', icon: 'Trash', title: 'License Deleted', desc: LIC_TYPE_LABEL[deleteLicConfirm.type] || deleteLicConfirm.type });
                setDeleteLicConfirm(null);
              } catch (err) { toast({ tone: 'error', icon: 'AlertCircle', title: 'Delete failed', desc: err.message }); }
            }}>Delete License</Btn>
          </div>
        </TenancyModal>
      )}

      {/* ── Add License modal ───────────────────────────────────── */}
      {addLicModal && (
        <TenancyModal title="Add License" icon="Key" dialog onClose={() => setAddLicModal(null)}
          footer={<>
            <Btn variant="ghost" onClick={() => setAddLicModal(null)}>Cancel</Btn>
            <Btn variant="primary" icon="Key" disabled={addLicSeats < 1} onClick={async () => {
              try {
                const renews = new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0];
                const res = await licenseApi.create(org.id, { type: addLicModal, allotted: addLicSeats, renews });
                const newAllotted = res?.allotted ?? addLicSeats;
                const newUsed     = res?.used     ?? 0;
                data.update(s => ({ ...s, licenses: [...s.licenses, {
                  id:        res?.id || ('lic_' + Date.now()),
                  org:       org.id,
                  type:      addLicModal,
                  status:    res?.status    || 'ACTIVE',
                  purchased: newAllotted,
                  used:      newUsed,
                  available: Math.max(0, newAllotted - newUsed),
                  renews:    res?.renews ? String(res.renews).split('T')[0] : renews,
                  reason:    null,
                }] }));
                data.addAudit({ action: LIC_TYPE_LABEL[addLicModal] + ' license added: ' + addLicSeats + ' seats', target: org.name, cat: 'License' });
                toast({ tone: 'success', icon: 'Key', title: 'License Added', desc: addLicSeats + ' ' + (LIC_TYPE_LABEL[addLicModal] || addLicModal) + ' seats' });
                setAddLicModal(null);
                setAddLicSeats(10);
              } catch (err) { toast({ tone: 'error', icon: 'AlertCircle', title: 'Create failed', desc: err.message }); }
            }}>Add License</Btn>
          </>}>
          <div className="nx-form">
            <div className="nx-form-row">
              <label>License Type</label>
              <select className="nx-input" value={addLicModal} onChange={e => setAddLicModal(e.target.value)}>
                <option value="USER">User Based</option>
                <option value="AGENT">Agent Based</option>
                <option value="BOT">Bot Based</option>
                <option value="WORKSPACE">Workspace</option>
                <option value="STORAGE">Storage</option>
                <option value="API">API</option>
                <option value="MESSAGE">Message</option>
                <option value="CUSTOM">Custom</option>
              </select>
            </div>
            <div className="nx-form-row">
              <label>Seat Count</label>
              <input type="number" min={1} step={1} className="nx-input" value={addLicSeats}
                onChange={e => setAddLicSeats(Math.max(1, Number(e.target.value)))} />
            </div>
            <p className="nx-muted nx-mini">Renewal will be set to 1 year from today.</p>
          </div>
        </TenancyModal>
      )}
    </div>
  );
}
